// Drafts of configs, kept in this browser (IndexedDB) as they're edited, so nothing's lost when the page closes; each
// held by one tab at a time (Web Locks), so two tabs can't write over each other. A draft keeps the file as text, and
// the library item it's a version of, once it is one.
export interface Draft {
  id: string;
  name: string;
  /** The config file, as it would be saved. */
  text: string;
  /** The library item it's the next version of, once saved or opened from the library. */
  itemId: string | null;
  /** The item's version this draft started from, or last saved: a newer one meanwhile is a clash. */
  baseN: number | null;
  /** It has changes that aren't in the library. */
  unsaved: boolean;
  updatedAt: string;
}

const DB = 'tune-configs';
const STORE = 'drafts';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE, { keyPath: 'id' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      // No IndexedDB here (a private window, say): drafts last while the page does.
      resolve(null);
    }
  });
}

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB failed'));
  });
}

/** While IndexedDB can't be had: drafts in the page. */
const memory = new Map<string, Draft>();

export async function listDrafts(): Promise<Draft[]> {
  const db = await open();
  const all = db ? ((await done(db.transaction(STORE).objectStore(STORE).getAll())) as Draft[]) : [...memory.values()];
  return all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function getDraft(id: string): Promise<Draft | null> {
  const db = await open();
  if (!db) {
    return memory.get(id) ?? null;
  }
  return ((await done(db.transaction(STORE).objectStore(STORE).get(id))) as Draft | undefined) ?? null;
}

export async function putDraft(draft: Draft): Promise<void> {
  const db = await open();
  if (!db) {
    memory.set(draft.id, draft);
    return;
  }
  await done(db.transaction(STORE, 'readwrite').objectStore(STORE).put(draft));
}

/** A new draft of a config's file, kept at once. */
export async function newDraft(
  text: string,
  o: { name: string; itemId?: string | null; baseN?: number | null },
): Promise<Draft> {
  const draft: Draft = {
    id: crypto.randomUUID(),
    name: o.name,
    text,
    itemId: o.itemId ?? null,
    baseN: o.baseN ?? null,
    unsaved: !o.itemId,
    updatedAt: new Date().toISOString(),
  };
  await putDraft(draft);
  return draft;
}

/** The newest draft of a library item, if this browser has one. */
export async function draftOf(itemId: string): Promise<Draft | null> {
  return (await listDrafts()).find((d) => d.itemId === itemId) ?? null;
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await open();
  memory.delete(id);
  if (db) {
    await done(db.transaction(STORE, 'readwrite').objectStore(STORE).delete(id));
  }
}

/** Every draft goes: the owner signed out, and the next person to use this browser shouldn't see them. */
export async function forgetDrafts(): Promise<void> {
  memory.clear();
  const db = await open();
  if (db) {
    await done(db.transaction(STORE, 'readwrite').objectStore(STORE).clear());
  }
}

interface Hold {
  /** Whether this tab has the draft's lock. */
  held: Promise<boolean>;
  users: number;
  free: () => void;
  /** Who's told when another tab takes it: one entry each time it's held, the same function or not. */
  lost: Set<{ told: () => void }>;
}

/** This tab's holds, shared by whatever in it holds the same draft (and by a page mounted twice in development). */
const holds = new Map<string, Hold>();

function lockFor(id: string, take: boolean): Hold {
  const hold: Hold = { held: Promise.resolve(true), users: 0, free: () => undefined, lost: new Set() };
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) {
    return hold;
  }
  let freed = false;
  hold.held = new Promise<boolean>((answer) => {
    const options: LockOptions = take ? { steal: true } : { ifAvailable: true };
    locks
      .request(`tune-draft-${id}`, options, (lock) => {
        if (!lock) {
          answer(false);
          return undefined;
        }
        answer(true);
        // Held until freed.
        return new Promise<void>((resolve) => {
          hold.free = () => {
            freed = true;
            resolve();
          };
        });
      })
      .catch(() => {
        // Another tab took it.
        if (!freed && holds.get(id) === hold) {
          holds.delete(id);
          for (const entry of hold.lost) {
            entry.told();
          }
        }
      });
  });
  return hold;
}

/**
 * Holds a draft for this tab while it's open, so two tabs can't write over each other: another tab that opens it is
 * told it's open elsewhere, and can take it (`take`), when this tab hears it lost it (`onLost`). The answer says
 * whether this tab has it; `release` lets it go.
 */
export async function holdDraft(
  id: string,
  o: { take?: boolean; onLost?: () => void } = {},
): Promise<{ held: boolean; release: () => void }> {
  let hold = holds.get(id);
  if (!hold || o.take) {
    hold?.free();
    hold = lockFor(id, o.take ?? false);
    holds.set(id, hold);
  }
  const mine = hold;
  mine.users += 1;
  const entry = o.onLost ? { told: o.onLost } : null;
  if (entry) {
    mine.lost.add(entry);
  }
  let done = false;
  const release = () => {
    if (done) {
      return;
    }
    done = true;
    mine.users -= 1;
    if (entry) {
      mine.lost.delete(entry);
    }
    // A moment later, so a page mounted again at once keeps it.
    window.setTimeout(() => {
      if (mine.users === 0 && holds.get(id) === mine) {
        holds.delete(id);
        mine.free();
      }
    }, 0);
  };
  return { held: await mine.held, release };
}

/** How many drafts have changes that aren't in the library: signing out deletes them. */
export async function unsavedDrafts(): Promise<number> {
  try {
    return (await listDrafts()).filter((d) => d.unsaved).length;
  } catch {
    return 0;
  }
}
